import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import * as highcharts from "highcharts";
import { OnInit } from "@angular/core/src/metadata/lifecycle_hooks";
import Boost from "highcharts/modules/boost";
import "highcharts";
import { TimeProvider } from "../../providers/time/time";
import { LdsClientService } from "../../providers/lds-client/lds-client.service";
import { BehaviorSubject, Subject } from "rxjs";
import moment from "moment";
import * as Plotly from "plotly.js";
import { TagsService } from "../../providers/trend-live-services/tags.service";
import { TimeRange } from "../../models/trend-live-models/TimeRange";

@Component({
  selector: "chart",
  templateUrl: "chart.html",
})
export class ChartComponent implements OnInit {
  currentLdsData = [];
  _timeRange;
  @Input() set ldsTag(val) {
    if (val) {
      this._ldsTag = val;
    }
  }
  @Input() set train(val) {
    if (val) {
      this._train = val;
    }
  }
  @Input() set timeRange(val) {
    if (val) {
      this._timeRange = val;
      this.fetchLdsTagData();
      Plotly.purge(this.chartRef.nativeElement);
    }
  }
  @Input()
  set timezone(val) {
    if (val) {
      this._timezone = val;
    }
  }
  @Input() set subscription(value: string) {
    if (value) {
      this._subscription = value;
      this.subscriptionStarted.next(value);
    }
  }
  @Output() dataLoadedEvent = new EventEmitter<any>();
  @ViewChild("chart", { read: ElementRef }) chartRef: ElementRef;
  _train;
  _ldsTag: any;
  currentLiveValue;
  _liveDataCapabilites = false;
  liveDataAvailable = false;
  _timezone: string;
  currentLiveDate: any;
  currentValueIsBigger: boolean;
  fromNowDate: string;
  subscriptionStarted = new BehaviorSubject(undefined);
  _subscription;
  rendered = false;

  constructor(
    public time: TimeProvider,
    private lds: LdsClientService,
    private tags: TagsService
  ) {}

  ngOnInit(): void {
    // this.fetchLdsTagData();
  }

  fetchLdsTagData() {
    if (this._timeRange) {
      let newTimeRange = new TimeRange(this._timeRange, "d", true);
      this.tags
        .fetch(
          this._ldsTag,
          newTimeRange,
          { value: "optim", name: "optim" },
          true
        )
        .subscribe((tagEntries) => {
          this.currentLdsData = tagEntries.map((entry) => {
            let parsedToUtc = moment.utc(entry.timestamp).format();

            return [parsedToUtc, entry.value];
          });

          this.createChart();
        });
      // this.tags.fetch(this.)
    }
  }

  createChart() {
    let plotlyData = this.adaptDataToPLotly(this.currentLdsData);
    const layout = this.getLayoutAndConfig().layout;
    const config = this.getLayoutAndConfig().config;

    // this.enableLiveData();
    Plotly.newPlot(
      this.chartRef.nativeElement,
      plotlyData,
      layout,
      config
    ).then((val) => {
      this.dataLoadedEvent.emit(this._ldsTag.tagAlias);
    });
  }

  adaptDataToPLotly(rawData) {
    let plotlyData = [];
    if (rawData) {
      plotlyData[0] = {
        x: rawData.map((d) => d[0]),
        y: rawData.map((i) => i[1]),
        line: {
          color: "rgb(77, 33, 122)",
          width: 2,
        },
      };
    }
    return plotlyData;
  }

  streamDataToPlotly(streamData) {
    let plotlyData = [];
    if (streamData) {
      plotlyData[0] = {
        x: streamData.value.responses.map((i) => {
          return this.tags.toTimezone(i, this._timezone);
        }),
        y: streamData.value.responses.map((i) => i.value),
        line: {
          color: "rgb(77, 33, 122)",
          width: 2,
        },
      };
    }
    return plotlyData;
  }

  enableLiveData() {
    if (this._ldsTag.ready === true) {
      this.subscriptionStarted.subscribe((sub) => {
        this.lds.stream.subscribe((stream) => {
          if (
            stream.subscriptionId === this._subscription &&
            stream.value.responses.length
          ) {
            // Trace update
            const update = {
              x: [],
              y: [],
            };
            this.liveDataAvailable = true;
            const index = stream.value.responses.findIndex(
              (streamTag) => streamTag.tag === this._ldsTag.tagName
            );
            if (index > -1) {
              const currentResponseData = stream.value.responses[index];

              this.currentLiveValue = Number(
                currentResponseData.value.toFixed(4)
              );
              let timezoneDate = this.tags.toTimezone(
                currentResponseData.origin,
                this._timezone
              );
              this.currentLiveDate = moment(timezoneDate).format(
                "YYYY-MM-DD HH:mm:ss"
              );
              let traceIndices = [];

              update.x.push([this.currentLiveDate]);
              update.y.push([this.currentLiveValue]);
              if (this.currentLdsData) {
                const allCurrentData = this.currentLdsData;
                if (
                  currentResponseData.value >
                  allCurrentData[allCurrentData.length - 1][1]
                ) {
                  this.currentValueIsBigger = true;
                } else {
                  this.currentValueIsBigger = false;
                }
              }
              traceIndices.push(0);

              try {
                // Add new points
                this.dataLoadedEvent.emit(true);
                Plotly.extendTraces(
                  this.chartRef.nativeElement,
                  update,
                  traceIndices
                );
              } catch (e) {
                console.log(e, update, traceIndices);
              }
            }
          }
        });
      });
    }
  }

  getLayoutAndConfig() {
    const layout = {
      dragMode: false,
      font: { size: 10, family: "Siemens Slab" },
      height: 230,
      hovermode: "x",
      hoverdistance: 100, // Distance to show hover label of data point
      spikedistance: 1000,
      margin: {
        l: 0,
        r: 0,
        // b: 0,
        t: 0,
        pad: 15,
      },
      xaxis: {
        type: "date",
        showspikes: true,
        showgrid: true,
        tickfont: {
          family: "Siemens Slab",
          size: 12,
          color: "#4d217a",
        },
      },
      displayModeBar: true,
      yaxis: {
        automargin: true,
        fixedrange: false,
        autorange: true,
        showgrid: true,
        // "showspikes": true,
        side: "right",
        title: {
          text: this._ldsTag.unit,
          font: {
            family: "Siemens Slab",
            size: 18,
          },
        },
      },
    };
    const config = {
      responsive: true,
      displayModeBar: false,
      scrollZoom: true,
    };
    return { layout: layout, config: config };
  }
}



// WEBPACK FOOTER //
// ./src/components/chart/chart.ts